import {
  APP_USERNAME_REGEX,
  DEFAULT_AVATAR,
  isAvatarId,
  NICKNAME_MAX,
  sanitizeNickname,
  updateProfileSchema,
  zeroUserStats,
  type AvatarId,
} from "@bing/shared";
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type SVGProps,
} from "react";
import { ApiError } from "../api/client";
import { Avatar } from "../components/Avatar";
import { AvatarCropper } from "../components/AvatarCropper";
import { AvatarFileError, prepareAvatarUpload } from "../components/avatar-upload";
import { AvatarPicker } from "../components/AvatarPicker";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { StatCard } from "../components/GameStats";
import { profileDisplayName } from "../components/profile-ui";
import { ScreenTitle } from "../components/ScreenTitle";
import { OfficialBadge } from "../components/social-ui";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";
import { useSession } from "../store/context";
import type { View } from "../store/session";

type SaveState = "idle" | "saving" | "saved" | "error";
type UsernameStatus = "idle" | "invalid" | "checking" | "available" | "taken" | "reserved";
type Toast = { text: string; kind: "ok" | "err" } | null;

/** The precise Persian reason an avatar upload failed (never a bare English message). */
function uploadErrorText(error: unknown): string {
  if (error instanceof AvatarFileError) {
    return error.issue === "too_large" ? fa.profile.photoTooLarge : fa.profile.photoUnsupported;
  }
  if (error instanceof ApiError) {
    if (error.status === 413) return fa.profile.photoTooLarge;
    if (error.status === 400) return fa.profile.photoUnsupported;
  }
  return fa.profile.uploadError;
}

type IconName =
  | "back"
  | "edit"
  | "person"
  | "at"
  | "volume"
  | "bell"
  | "check"
  | "camera"
  | "image"
  | "settings"
  | "history";

/** Inline `currentColor` SVGs standing in for the Stitch Material Symbols. */
function Icon({ name, className }: { name: IconName; className?: string }): ReactNode {
  const svg: SVGProps<SVGSVGElement> = {
    viewBox: "0 0 24 24",
    fill: "currentColor",
    className,
    "aria-hidden": true,
  };
  switch (name) {
    case "back": // RTL "back" points to the right (Material arrow_forward)
      return (
        <svg {...svg}>
          <path d="M12 4l-1.4 1.4L16.2 11H4v2h12.2l-5.6 5.6L12 20l8-8z" />
        </svg>
      );
    case "edit":
      return (
        <svg {...svg}>
          <path d="M4 17.25V20h2.75L16.8 9.94l-2.75-2.75L4 17.25zm15.7-10.2a.75.75 0 0 0 0-1.06l-1.69-1.69a.75.75 0 0 0-1.06 0l-1.32 1.32 2.75 2.75 1.32-1.32z" />
        </svg>
      );
    case "person":
      return (
        <svg {...svg}>
          <circle cx="12" cy="8" r="3.7" />
          <path d="M4.8 19.4c0-3.4 3.3-5.5 7.2-5.5s7.2 2.1 7.2 5.5v.6H4.8z" />
        </svg>
      );
    case "at":
      return (
        <svg {...svg}>
          <path d="M12 2a10 10 0 1 0 4.3 19l-.85-1.8A8 8 0 1 1 20 12c0 1.5-.8 2.5-2 2.5s-1.8-1-1.8-2.3V8h-2v.9A4 4 0 1 0 12 16a3.8 3.8 0 0 0 3-1.4c.6 1 1.7 1.6 3 1.6 2.4 0 4-2 4-4.2A10 10 0 0 0 12 2zm0 12a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
        </svg>
      );
    case "volume":
      return (
        <svg {...svg}>
          <path d="M3.5 9.5v5h3.2l4.3 4.3V5.2L6.7 9.5H3.5z" />
          <path
            d="M14.5 8.6a4.2 4.2 0 0 1 0 6.8M16.9 6.1a7.4 7.4 0 0 1 0 11.8"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
          />
        </svg>
      );
    case "bell":
      return (
        <svg {...svg}>
          <path d="M12 22a2.2 2.2 0 0 0 2.2-2.2H9.8A2.2 2.2 0 0 0 12 22zm6-5.8V11c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.63 5.36 6 7.92 6 11v5.2L4.2 18v.9h15.6V18L18 16.2z" />
        </svg>
      );
    case "check":
      return (
        <svg {...svg}>
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.3 14.5-3.8-3.8 1.4-1.4 2.4 2.4 5-5 1.4 1.4-6.4 6.4z" />
        </svg>
      );
    case "camera":
      return (
        <svg {...svg}>
          <path d="M9.4 5l-1.2 1.6H5A2 2 0 0 0 3 8.6v9A2 2 0 0 0 5 19.6h14a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-3.2L14.6 5H9.4zM12 16.6a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
        </svg>
      );
    case "image":
      return (
        <svg {...svg}>
          <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm0 14.5L9 12l3 3.6L14.5 12l4.5 5.5H5z" />
          <circle cx="8.5" cy="8" r="1.6" />
        </svg>
      );
    case "settings": // gear
      return (
        <svg {...svg}>
          <path d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7 7 0 0 0-1.63-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54c-.59.24-1.13.56-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.74 8.84a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.42.34.6.22l2.39-.96c.5.38 1.04.7 1.63.94l.36 2.54c.05.24.25.42.5.42h3.84c.25 0 .45-.18.5-.42l.36-2.54c.59-.24 1.13-.56 1.63-.94l2.39.96c.18.12.46.02.6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z" />
        </svg>
      );
    case "history": // clock with a reverse arrow
      return (
        <svg {...svg}>
          <path d="M13 3a9 9 0 0 0-9 9H1l3.9 3.9.1.14L9 12H6a7 7 0 1 1 2.05 4.95l-1.42 1.42A9 9 0 1 0 13 3zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
        </svg>
      );
  }
}

/** A clean ×-in-circle glyph for the "remove display name" control. */
function ClearNameGlyph({ className }: { className?: string }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6" />
    </svg>
  );
}

/** Transient success/error banner, auto-dismissed by the screen. */
function ToastBanner({ toast }: { toast: NonNullable<Toast> }): ReactNode {
  return (
    <div
      role="status"
      className={`fixed inset-x-0 top-4 z-[80] mx-auto flex w-max max-w-[90%] items-center gap-2 rounded-full px-4 py-2 font-body-md text-body-md shadow-lg ${
        toast.kind === "ok" ? "bg-success-win/90 text-ink-on-gold" : "bg-error/90 text-on-surface"
      }`}
    >
      {toast.text}
    </div>
  );
}

/**
 * One field label around a text input, with an optional themed leading `icon` on
 * the (RTL-start) right and/or a fixed `leftAdornment` (e.g. the username "@") on
 * the physical left, before where an `dir="ltr"` input's text begins.
 */
function Field({
  id,
  label,
  icon,
  leftAdornment,
  children,
}: {
  id: string;
  label: string;
  icon?: IconName;
  leftAdornment?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col gap-xs">
      <label htmlFor={id} className="font-label-md text-label-md text-on-surface-variant px-sm">
        {label}
      </label>
      <div className="relative">
        {icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant">
            <Icon name={icon} className="h-5 w-5" />
          </span>
        ) : null}
        {leftAdornment ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-body-lg text-body-lg text-on-surface-variant">
            {leftAdornment}
          </span>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export function Profile(): ReactNode {
  const profile = useSession((s) => s.profile);
  const stats = useSession((s) => s.stats) ?? profile?.stats ?? zeroUserStats();
  const saveProfile = useSession((s) => s.saveProfile);
  const checkUsername = useSession((s) => s.checkUsername);
  const uploadAvatar = useSession((s) => s.uploadAvatar);
  const removeAvatarPhoto = useSession((s) => s.removeAvatarPhoto);
  const loadStats = useSession((s) => s.loadStats);
  const setView = useSession((s) => s.setView);
  const setPageReturn = useSession((s) => s.setPageReturn);
  const setNavGuard = useSession((s) => s.setNavGuard);
  // The view the user tried to navigate to while there are unsaved edits — set by the
  // nav-guard, which drives the "unsaved changes" confirm.
  const [pendingLeave, setPendingLeave] = useState<View | null>(null);

  const [username, setUsername] = useState(profile?.appUsername ?? "");
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>("idle");
  const [nickname, setNickname] = useState(profile?.nickname ?? "");
  const [avatarId, setAvatarId] = useState<AvatarId>(
    profile && isAvatarId(profile.avatarId) ? profile.avatarId : DEFAULT_AVATAR,
  );
  // True after the user taps a preset while a photo is uploaded: the hero then
  // PREVIEWS the preset (photo hidden), and «ذخیره» makes it real by removing the
  // photo — the one mechanism for going back from a photo to a preset avatar.
  const [presetPreview, setPresetPreview] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  // A picked (validated/transcoded) photo awaiting the user's crop: the object
  // URL feeds the cropper's <img>; the blob is the no-canvas upload fallback.
  const [pendingCrop, setPendingCrop] = useState<{
    url: string;
    blob: Blob;
    mime: string;
  } | null>(null);

  // The hero avatar is the single tap-target for picking/changing the photo, so one
  // hidden picker suffices (accept="image/*" lets the OS offer camera OR gallery).
  const galleryRef = useRef<HTMLInputElement>(null);

  // Load the stats totals for the hero row (games/wins/losses). The full per-game
  // breakdown + match history live on the dedicated «تاریخچه» screen (a button below).
  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  // Auto-dismiss the transient toast.
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(id);
  }, [toast]);

  // Debounced app-username availability check (skips empty / unchanged / malformed).
  useEffect(() => {
    const value = username.trim();
    const current = profile?.appUsername ?? "";
    if (!value || value.toLowerCase() === current.toLowerCase()) {
      setUsernameStatus("idle");
      return;
    }
    if (!APP_USERNAME_REGEX.test(value)) {
      setUsernameStatus("invalid");
      return;
    }
    setUsernameStatus("checking");
    let cancelled = false;
    const id = window.setTimeout(() => {
      void checkUsername(value)
        .then((res) => {
          if (cancelled) return;
          setUsernameStatus(
            res.available ? "available" : res.reason === "reserved" ? "reserved" : "taken",
          );
        })
        .catch(() => {
          if (!cancelled) setUsernameStatus("idle");
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [username, profile, checkUsername]);

  // Revoke the crop preview's object URL once it is replaced or unmounted.
  useEffect(() => {
    const url = pendingCrop?.url;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [pendingCrop]);

  // ── Unsaved-changes guard ─────────────────────────────────────────────────
  // "Dirty" = a Save-button field differs from what's persisted (nickname/username/
  // avatar-preset). The settings toggles (sound / notifications) and the avatar PHOTO
  // upload persist immediately, so they're never pending — excluded here.
  const savedAvatarId = profile && isAvatarId(profile.avatarId) ? profile.avatarId : DEFAULT_AVATAR;
  const dirty =
    sanitizeNickname(nickname) !== (profile?.nickname ?? "") ||
    username.trim().toLowerCase() !== (profile?.appUsername ?? "").toLowerCase() ||
    presetPreview ||
    avatarId !== savedAvatarId;

  // Register a leave-guard while dirty: EVERY `setView` exit (in-app back, bottom nav,
  // the Telegram back button which routes through setView) is vetoed and remembered as
  // `pendingLeave` → the confirm dialog. No edits → no guard → leave freely.
  useEffect(() => {
    if (!dirty) {
      setNavGuard(null);
      return;
    }
    setNavGuard((next) => {
      if (next === "profile") return true; // staying in / returning to profile is fine
      setPendingLeave(next);
      return false; // block; the dialog drives the actual navigation
    });
    return () => setNavGuard(null);
  }, [dirty, setNavGuard]);

  // NO BACK BUTTON HERE — neither ours nor Telegram's.
  //
  // Profile is a BOTTOM-NAV TAB (games / friends / chat / profile), so there is always
  // a way out: tap another tab. Mounting Telegram's BackButton on a root tab turned its
  // top-left CLOSE control into a back arrow, which is what the user saw — the app's own
  // header back button (removed earlier) was the duplicate, and this was the survivor.
  // Every exit still routes through `setView`, so the unsaved-changes guard above catches
  // all of them.

  /** Pick → validate/normalize client-side (5 MB mirror + webp/HEIC → JPEG so
   * the preview is decodable) → open the crop sheet. Nothing uploads yet. */
  async function handlePick(file: File): Promise<void> {
    try {
      const prepared = await prepareAvatarUpload(file);
      setPendingCrop({ url: URL.createObjectURL(prepared.blob), ...prepared });
    } catch (error) {
      setToast({ text: uploadErrorText(error), kind: "err" });
    }
  }

  /** «ذخیره» in the cropper: upload the framed square — or, when the platform
   * couldn't render one (null), the original blob; the server's center-crop is
   * a no-op on the already-square image and the safety net otherwise. */
  async function handleCropSave(cropped: Blob | null): Promise<void> {
    const pending = pendingCrop;
    if (!pending) return;
    setPendingCrop(null); // close the sheet; the hero shows the uploading state
    setUploading(true);
    try {
      // The store flips `profile.avatarPhotoId` only on server confirm.
      await uploadAvatar(cropped ?? pending.blob, cropped ? "image/jpeg" : pending.mime);
      setPresetPreview(false); // a fresh photo always wins over a pending preset pick
      setToast({ text: fa.profile.uploadSuccess, kind: "ok" });
    } catch (error) {
      setToast({ text: uploadErrorText(error), kind: "err" });
    } finally {
      setUploading(false);
    }
  }

  function onPick(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (file) void handlePick(file);
  }

  async function onSave(): Promise<boolean> {
    const uname = username.trim();
    // A nickname is a permissive display label: sanitize like the server, then decide
    // the patch. An EMPTIED field means "clear the custom name" (revert to the Telegram
    // name) — we send it (empty) only when it actually changed; the server normalizes
    // an empty value to null. Unchanged → omit it (no write). Only limit is length.
    const nick = sanitizeNickname(nickname);
    const currentNick = profile?.nickname ?? "";
    const nicknameChanged = nick !== currentNick;
    const currentUname = profile?.appUsername ?? "";
    const includeUsername = uname !== "" && uname.toLowerCase() !== currentUname.toLowerCase();

    // Persian pre-validation — never surface a raw English schema/server message.
    if (includeUsername && !APP_USERNAME_REGEX.test(uname)) {
      setErrorMsg(fa.profile.usernameInvalid);
      setSaveState("error");
      return false;
    }
    if (includeUsername && usernameStatus === "taken") {
      setErrorMsg(fa.profile.usernameTaken);
      setSaveState("error");
      return false;
    }
    if (includeUsername && usernameStatus === "reserved") {
      setErrorMsg(fa.profile.usernameReserved);
      setSaveState("error");
      return false;
    }
    // The ONLY nickname limit is the 64-code-point length (an empty value = clear).
    if (nicknameChanged && nick !== "" && [...nick].length > NICKNAME_MAX) {
      setErrorMsg(fa.profile.nicknameTooLong);
      setSaveState("error");
      return false;
    }

    const payload = {
      avatarId,
      ...(nicknameChanged ? { nickname: nick } : {}),
      ...(includeUsername ? { appUsername: uname } : {}),
    };
    const parsed = updateProfileSchema.safeParse(payload);
    if (!parsed.success) {
      // Backstop for anything the checks above missed, still in Persian.
      const field = parsed.error.issues[0]?.path[0];
      setErrorMsg(
        field === "appUsername"
          ? fa.profile.usernameInvalid
          : field === "nickname"
            ? fa.profile.nicknameTooLong // the only nickname failure left is the length cap
            : fa.profile.saveError,
      );
      setSaveState("error");
      return false;
    }

    setSaveState("saving");
    setErrorMsg(null);
    try {
      // Persist the profile FIRST. Only if it succeeds do we drop the uploaded photo (going back
      // to a preset). The reverse order silently deleted the photo even when the save then failed
      // (e.g. a taken/reserved username 409) — the user saw "username taken" and believed nothing
      // changed, but their photo was already gone. Now a failed save leaves the photo intact.
      await saveProfile(parsed.data);
      if (presetPreview && profile?.avatarPhotoId) await removeAvatarPhoto();
      // Reflect EXACTLY what was persisted so a leading/trailing space (or an odd/
      // cleared value) doesn't linger in the field — the schema trimmed + collapsed the
      // nickname (or normalized a cleared one to null) and lowercased the username.
      if ("nickname" in parsed.data) setNickname(parsed.data.nickname ?? "");
      if (parsed.data.appUsername !== undefined) setUsername(parsed.data.appUsername);
      setPresetPreview(false);
      setSaveState("saved");
      setToast({ text: fa.profile.saved, kind: "ok" });
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === "USERNAME_RESERVED") {
        // The handle is a reserved platform/staff/system name.
        setErrorMsg(fa.profile.usernameReserved);
        setUsernameStatus("reserved");
      } else if (error instanceof ApiError && error.status === 409) {
        // A 409 now only comes from the (unique) username — a display name isn't unique.
        setErrorMsg(fa.profile.usernameTaken);
        setUsernameStatus("taken");
      } else {
        setErrorMsg(fa.profile.saveError);
      }
      setSaveState("error");
      return false;
    }
  }

  /** «ذخیره»: save, then (only on success) leave to where the user was headed. On a
   *  validation/save failure, close the dialog so the error is visible; they stay dirty. */
  async function saveThenLeave(): Promise<void> {
    const target = pendingLeave;
    const ok = await onSave();
    setPendingLeave(null);
    if (ok && target) {
      setNavGuard(null); // the effect will clear it too once dirty recomputes; be explicit
      setView(target);
    }
  }

  /** «لغو تغییرات»: drop the edits and leave (Profile remounts clean from `profile`). */
  function discardThenLeave(): void {
    const target = pendingLeave;
    setPendingLeave(null);
    setNavGuard(null);
    if (target) setView(target);
  }

  const saveLabel =
    saveState === "saving"
      ? fa.profile.saving
      : saveState === "saved"
        ? fa.profile.saved
        : fa.profile.save;

  // The hero name previews the draft nickname; when the field is EMPTY it shows what
  // clearing reverts to — the Telegram first+last name (nickname excluded from the
  // resolution), so the revert is visible before you even save.
  const revertName = profileDisplayName(profile ? { ...profile, nickname: null } : null);
  const displayName = nickname.trim() || revertName;
  // The hero subline is the APP handle only (the identity users search by).
  // The Telegram @username and the numeric telegramId are identifiers, not
  // names — they never render (the old `#<telegramId>` fallback leaked ids).
  const draftHandle = username.trim() || profile?.appUsername || "";
  const handle = draftHandle ? `@${draftHandle}` : null;

  const usernameNote: { text: string; className: string } | null =
    usernameStatus === "checking"
      ? { text: fa.profile.usernameChecking, className: "text-on-surface-variant" }
      : usernameStatus === "available"
        ? { text: fa.profile.usernameAvailable, className: "text-success-win" }
        : usernameStatus === "taken"
          ? { text: fa.profile.usernameTaken, className: "text-error" }
          : usernameStatus === "reserved"
            ? { text: fa.profile.usernameReserved, className: "text-error" }
            : usernameStatus === "invalid"
              ? { text: fa.profile.usernameInvalid, className: "text-error" }
              : null;

  return (
    <div
      className="flex min-h-full flex-col bg-background text-on-surface"
      style={{
        backgroundImage:
          "radial-gradient(circle at top, rgb(var(--c-room-light) / 0.8) 0%, rgb(var(--c-surface)) 80%)",
      }}
      dir="rtl"
    >
      {toast ? <ToastBanner toast={toast} /> : null}

      {/* Unsaved-changes guard: trying to leave Profile with pending edits (any exit
          path — in-app back, bottom nav, or the Telegram/hardware back button) prompts
          Save / Discard / Stay. Reusing the shared 3-action ConfirmDialog. */}
      {pendingLeave ? (
        <ConfirmDialog
          title={fa.profile.unsavedTitle}
          body={fa.profile.unsavedBody}
          confirmLabel={fa.profile.unsavedSave}
          onConfirm={() => void saveThenLeave()}
          danger={false}
          secondaryLabel={fa.profile.unsavedDiscard}
          onSecondary={discardThenLeave}
          secondaryDanger
          cancelLabel={fa.profile.unsavedStay}
          onCancel={() => setPendingLeave(null)}
        />
      ) : null}

      {/* Crop sheet: frame the picked photo before anything uploads. */}
      {pendingCrop ? (
        <AvatarCropper
          src={pendingCrop.url}
          onSave={(cropped) => void handleCropSave(cropped)}
          onCancel={() => setPendingCrop(null)}
        />
      ) : null}

      {/* TopAppBar. NO in-app back button: Telegram's own back arrow is shown while this
          screen is open (the effect above mounts it), so a second one in the corner was
          just a duplicate — and it routes through the SAME `setView`, so the
          unsaved-changes guard still catches every way out. Centred title, exactly like
          the other title screens (Friends/Chat). */}
      <header
        className="sticky top-0 z-20 flex items-center justify-center px-md h-16 bg-surface/90 border-b border-outline-variant/30"
        style={{ backdropFilter: "blur(12px)" }}
      >
        <ScreenTitle icon="profile" title={fa.profile.title} />
      </header>

      <main className="mx-auto flex w-full max-w-container-max flex-col gap-md px-md py-md pb-6">
        {/* Identity block */}
        <section className="relative flex flex-col items-center gap-sm overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-high/60 p-md">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-secondary/20 rounded-full blur-2xl" />
          {/* Hero avatar (photo when uploaded, else the picked preset). It IS the
              tap-target to pick/change the photo — no separate photo control. */}
          <button
            type="button"
            aria-label={fa.profile.editAvatar}
            onClick={() => galleryRef.current?.click()}
            disabled={uploading}
            className="relative z-10 appearance-none border-0 bg-transparent p-0 cursor-pointer disabled:opacity-70"
          >
            <Avatar
              avatarId={avatarId}
              avatarPhotoId={presetPreview ? null : profile?.avatarPhotoId}
              size={80}
              ringClass="border-secondary shadow-[0_8px_16px_rgba(0,0,0,0.6)]"
            />
            <span
              aria-hidden="true"
              className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border border-outline-variant bg-surface-container-high text-secondary"
            >
              <Icon name="camera" className="h-[18px] w-[18px]" />
            </span>
          </button>
          {/* The lone hidden picker the avatar tap opens. */}
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            aria-label={fa.profile.editAvatar}
            onChange={onPick}
            className="hidden"
          />
          {uploading ? (
            <p className="z-10 m-0 font-body-md text-body-md text-on-surface-variant">
              {fa.profile.uploading}
            </p>
          ) : null}
          <div className="z-10 text-center">
            <h2 className="m-0 flex items-center justify-center gap-1.5 font-display-lg-mobile text-display-lg-mobile text-on-surface">
              {displayName}
              {profile?.isOfficial ? <OfficialBadge size={22} /> : null}
            </h2>
            {handle ? (
              <p className="m-0 mt-xs font-body-md text-body-md text-on-surface-variant" dir="ltr">
                {handle}
              </p>
            ) : null}
          </div>
          {/* Stats summary row — compact cards (smaller than the History screen's) so the
              games/wins/losses take less space and the whole profile fits one page. */}
          <div className="z-10 mt-xs flex w-full max-w-[300px] justify-center gap-sm">
            <StatCard
              label={fa.profile.games}
              value={stats.gamesPlayed}
              accent="text-primary"
              compact
            />
            <StatCard label={fa.profile.wins} value={stats.wins} accent="text-secondary" compact />
            <StatCard label={fa.profile.losses} value={stats.losses} accent="text-error" compact />
          </div>
        </section>

        {/* Editable fields */}
        <section className="flex flex-col gap-sm">
          {/* App username — a fixed "@" prefix sits to the LEFT of the input, where
              the LTR handle starts (Telegram-style), so the user types after it. */}
          <Field id="app-username" label={fa.profile.username} leftAdornment="@">
            <input
              id="app-username"
              type="text"
              dir="ltr"
              value={username}
              placeholder={fa.profile.usernamePlaceholder}
              maxLength={32}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => {
                setUsername(e.target.value);
                setSaveState("idle");
                setErrorMsg(null);
              }}
              className="w-full appearance-none rounded-lg border border-outline-variant bg-surface-container-high py-sm pl-8 pr-3 font-body-lg text-body-lg text-on-surface text-left outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </Field>
          <p className="m-0 -mt-1 px-sm font-caption text-caption text-on-surface-variant">
            {usernameNote ? (
              <span className={usernameNote.className}>{usernameNote.text}</span>
            ) : (
              fa.profile.usernameHint
            )}
          </p>

          {/* Display name */}
          <Field id="display-name" label={fa.profile.nickname} icon="person">
            <input
              id="display-name"
              type="text"
              dir="rtl"
              value={nickname}
              placeholder={fa.profile.nicknamePlaceholder}
              maxLength={64}
              onChange={(e) => {
                setNickname(e.target.value);
                setSaveState("idle");
              }}
              className="w-full appearance-none rounded-lg border border-outline-variant bg-surface-container-high py-sm pl-md pr-10 font-body-lg text-body-lg text-on-surface text-right outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </Field>
          {/* Clear the custom display name → revert to the Telegram name. A polished
              pill under the field (RTL-start), shown only when there IS one to remove
              (saved or a live draft). Empties the field; Save persists the cleared state.
              Subtle by default, error-tinted on press — on-theme in light + dark. */}
          {(profile?.nickname ?? "") !== "" || nickname.trim() !== "" ? (
            <button
              type="button"
              aria-label={fa.profile.nicknameClear}
              onClick={() => {
                setNickname("");
                setSaveState("idle");
                playSfx("tap");
              }}
              className="mt-1.5 inline-flex items-center gap-1.5 self-start rounded-full border border-outline-variant/50 bg-surface-container-high px-3 py-1 font-label-md text-label-md text-on-surface-variant shadow-sm transition-all duration-150 hover:border-error/40 hover:bg-error/10 hover:text-error active:scale-95 active:bg-error/15"
            >
              <ClearNameGlyph className="h-3.5 w-3.5" />
              <span>{fa.profile.nicknameClear}</span>
            </button>
          ) : null}

          {/* Preset avatar (fallback when no photo is uploaded) */}
          <div className="mt-sm flex flex-col gap-sm">
            <label className="font-label-md text-label-md text-on-surface-variant px-sm">
              {fa.profile.presets}
            </label>
            <AvatarPicker
              value={avatarId}
              onChange={(next) => {
                setAvatarId(next);
                // With a photo uploaded the presets were dead controls (the photo
                // always won); picking one now previews it in the hero, and Save
                // persists the switch by removing the photo.
                setPresetPreview(true);
                setSaveState("idle");
              }}
            />
          </div>

          {errorMsg ? (
            <p className="font-body-md text-body-md text-error m-0 px-sm">{errorMsg}</p>
          ) : null}
        </section>

        {/* Save */}
        <button
          type="button"
          disabled={saveState === "saving"}
          onClick={() => void onSave()}
          style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.3)" }}
          className="flex w-full items-center justify-center gap-xs appearance-none cursor-pointer rounded-lg bg-secondary py-sm font-headline-sm text-headline-sm text-ink-on-gold shadow-[0_4px_12px_rgba(232,194,104,0.3)] transition-all hover:bg-secondary-fixed active:scale-[0.98] disabled:opacity-70"
        >
          <span>{saveLabel}</span>
          <Icon name="check" className="h-5 w-5" />
        </button>

        {/* «تنظیمات» + «تاریخچه» — NOT profile fields, so they sit behind two equal
            buttons in one row that navigate to their own screens (the per-game stats +
            match history now live on «تاریخچه», keeping this page short). The
            unsaved-changes guard still catches the exit. */}
        <div className="grid grid-cols-2 gap-sm">
          {(
            [
              { icon: "settings", label: fa.profile.openSettings, to: "settings" },
              { icon: "history", label: fa.profile.openHistory, to: "history" },
            ] as const
          ).map((b) => (
            <button
              key={b.to}
              type="button"
              onClick={() => {
                setPageReturn("profile"); // so Settings/History back to Profile, not the lobby
                setView(b.to);
              }}
              className="flex appearance-none cursor-pointer items-center justify-center gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-high py-3 font-body-lg text-body-lg text-on-surface shadow-sm transition-all hover:border-secondary/50 hover:text-secondary active:scale-95"
            >
              <span className="text-secondary">
                <Icon name={b.icon} className="h-5 w-5" />
              </span>
              {b.label}
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
